import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import type { CustomerStatementModel } from '../interfaces';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { CompanyEntity } from './company.entity';

/**
 * How a statement record came to exist. Stored as a varchar (not a DB enum) so
 * PD-14044 can add run-generated values without an ALTER on the shared column.
 */
export enum StatementGenerationSource {
	/** Generated one-off by a user (POST /api/statements/generate). */
	MANUAL = 'MANUAL',
	/** Generated by a statement run the daily schedule created (PD-14044). */
	SCHEDULED = 'SCHEDULED',
	/** Generated by a statement run a user queued by hand (PD-14044). */
	RUN_MANUAL = 'RUN_MANUAL',
	/** Generated by the cycle billing run as the bill preceding the charge (PD-15854). */
	CYCLE = 'CYCLE',
}

/**
 * PD-14041 — a STORED customer statement (foundation for real statements).
 *
 * Statements used to be rendered on demand (PD-13702) and could silently
 * change as new payments arrived. This table turns a generated statement into
 * a point-in-time record: `snapshotJson` holds the fully-rendered
 * CustomerStatementModel exactly as it looked at generation, and every future
 * on-screen view / PDF re-renders from that snapshot — no PDFs are stored, and
 * the record never changes after the fact.
 *
 * `endingBalance` / `totalAmountDue` are denormalized out of the snapshot so
 * list screens can filter/sort without parsing JSON. There is intentionally no
 * `statementNumber` column: no natural value exists before the row id is
 * assigned, so the human reference is derived as `ST-<id>` at read time.
 *
 * `distributedDate` is reserved for PD-14045 (statement distribution) and
 * stays null until a statement is actually sent to the customer.
 */
@Index('fk_client__id', ['fkClientId'], {})
@Index('fk_company__id', ['fkCompanyId'], {})
@Index('asOfDate', ['asOfDate'], {})
@Entity('statement', {}, { label: 'Statement' })
export class StatementEntity extends BaseEntity {
	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	/**
	 * The issuing company — the client's primary company
	 * (client.fk_company__id_primary) captured at generation time, so company
	 * scoping on statement lists matches how the client was owned when the
	 * statement was produced.
	 */
	@Column('int', { name: 'fk_company__id', unsigned: true })
	public fkCompanyId!: number;

	/**
	 * The statement's "as of" date (matches snapshotJson.statementDate) — the
	 * point-in-time stamp aging and the ledger cutoff were computed against.
	 */
	@Column('datetime', { name: 'asOfDate' })
	public asOfDate!: Date;

	/** Period window start; null = from the beginning of the client's history. */
	@Column('datetime', { name: 'periodStartDate', nullable: true })
	public periodStartDate!: Date | null;

	/** Period window end; null = through the as-of date. */
	@Column('datetime', { name: 'periodEndDate', nullable: true })
	public periodEndDate!: Date | null;

	/**
	 * The full rendered statement (header, monthly activity, line items,
	 * aging) exactly as built at generation time. The document of record —
	 * views and PDFs are re-rendered from this, never from live data.
	 */
	@Column('json', { name: 'snapshotJson' })
	public snapshotJson!: CustomerStatementModel;

	/** Running balance at the end of the statement (last month's endingBalance). */
	@Column('decimal', { name: 'endingBalance', precision: 13, scale: 2 })
	public endingBalance!: string;

	/** Total open balance as of the statement date (snapshot's aging total). */
	@Column('decimal', { name: 'totalAmountDue', precision: 13, scale: 2 })
	public totalAmountDue!: string;

	/** See {@link StatementGenerationSource}. */
	@Column('varchar', { name: 'generationSource', length: 32 })
	public generationSource!: string;

	/**
	 * The statement run that produced this statement (PD-14044); null for
	 * one-off generation. Plain FK column (no relation property) — runs are
	 * bookkeeping, nothing eager-loads through them.
	 */
	@Column('int', {
		name: 'fk_statement_run__id',
		nullable: true,
		unsigned: true,
	})
	public fkStatementRunId!: number | null;

	/** When the statement was sent to the customer (PD-14045); null until then. */
	@Column('datetime', { name: 'distributedDate', nullable: true })
	public distributedDate!: Date | null;

	/**
	 * PD-16863 — when the storefront users who can open this statement were
	 * told it is ready (bell and text); null until then, and for statements
	 * never announced (one-off CRM generation, or a storefront that does not
	 * show statements). It is set by an atomic NULL → now claim before
	 * anything is sent, so only one caller ever announces a statement, even
	 * when nobody turns out to be reachable.
	 */
	@Column('datetime', { name: 'portalNotifiedDate', nullable: true })
	public portalNotifiedDate!: Date | null;

	@ManyToOne(() => ClientEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	@ManyToOne(() => CompanyEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity;
}
